import { useState } from "react";
import { Download, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = ["Orders", "Messages", "Subscribers"];

const Stat = ({ label, value, testId }) => (
  <div className="bg-[#FAF7F2] border border-[#C6A769]/30 rounded-sm px-6 py-5 text-center" data-testid={testId}>
    <p className="font-serif-display text-3xl text-[#C6A769]">{value}</p>
    <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A3F35]/60 mt-1">{label}</p>
  </div>
);

const Th = ({ children }) => (
  <th className="px-5 py-4 font-medium text-[10px] uppercase tracking-[0.2em] text-[#C6A769] text-left">{children}</th>
);
const Td = ({ children, muted }) => (
  <td className={`px-5 py-3 text-sm ${muted ? "text-[#8A7D71]" : "text-[#4A3F35]"}`}>{children}</td>
);

export default function Admin() {
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Orders");

  const load = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.get("/admin/overview", { params: { key } });
      setData(res.data);
    } catch {
      toast.error("Invalid access key.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-page" className="pt-20 min-h-screen bg-[#F8F5F0]">
      <section className="max-w-5xl mx-auto px-6 py-24">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C6A769] mb-6 text-center">Private</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#4A3F35] mb-8 text-center">Dashboard</h1>
        <div className="w-16 h-px bg-[#C6A769] mx-auto mb-12" />

        {!data ? (
          <form onSubmit={load} className="max-w-md mx-auto space-y-5" data-testid="admin-key-form">
            <div className="flex items-center gap-3 text-[#8A7D71] text-sm justify-center">
              <Lock className="w-4 h-4" strokeWidth={1.5} /> Enter your access key
            </div>
            <input
              type="password"
              required
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Access key"
              data-testid="admin-key-input"
              className="w-full bg-[#FAF7F2] border border-[#4A3F35]/15 rounded-sm px-5 py-4 text-sm text-[#4A3F35] outline-none focus:border-[#C6A769] transition-colors text-center"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-key-submit"
              className="w-full bg-[#4A3F35] text-[#FAF7F2] py-4 rounded-sm text-sm uppercase tracking-[0.18em] hover:bg-[#C6A769] transition-colors duration-300 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : "Open Dashboard"}
            </button>
          </form>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <Stat label="Orders" value={data.stats.orders} testId="stat-orders" />
              <Stat label="Revenue" value={`$${(data.stats.revenue_cents / 100).toFixed(2)}`} testId="stat-revenue" />
              <Stat label="Messages" value={data.stats.messages} testId="stat-messages" />
              <Stat label="Subscribers" value={data.stats.subscribers} testId="stat-subscribers" />
            </div>

            <div className="flex items-center gap-2 mb-6">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  data-testid={`admin-tab-${t.toLowerCase()}`}
                  className={`px-6 py-2.5 rounded-sm text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                    tab === t ? "bg-[#4A3F35] text-[#FAF7F2]" : "bg-[#FAF7F2] text-[#4A3F35] border border-[#C6A769]/30 hover:border-[#C6A769]"
                  }`}
                >
                  {t}
                </button>
              ))}
              {tab === "Subscribers" && (
                <a
                  href={`${API_BASE}/admin/subscribers.csv?key=${encodeURIComponent(key)}`}
                  data-testid="download-csv-button"
                  className="ml-auto inline-flex items-center gap-2 bg-[#C6A769] text-[#4A3F35] px-5 py-2.5 rounded-sm text-xs uppercase tracking-[0.18em] hover:bg-[#B08F50] transition-colors duration-300"
                >
                  <Download className="w-4 h-4" strokeWidth={1.5} /> CSV
                </a>
              )}
            </div>

            <div className="bg-[#FAF7F2] border border-[#C6A769]/30 rounded-sm overflow-x-auto">
              {tab === "Orders" && (
                <table className="w-full" data-testid="orders-table">
                  <thead><tr className="border-b border-[#C6A769]/20">
                    <Th>Order</Th><Th>Email</Th><Th>Items</Th><Th>Total</Th><Th>Email Sent</Th><Th>Date</Th>
                  </tr></thead>
                  <tbody>
                    {data.orders.map((o, i) => (
                      <tr key={i} className="border-b border-[#4A3F35]/5 last:border-0">
                        <Td>{o.order_number || "—"}</Td>
                        <Td>{o.email}</Td>
                        <Td muted>{o.items}</Td>
                        <Td>${(o.amount_total / 100).toFixed(2)}</Td>
                        <Td muted>{o.email_sent ? "Yes" : "No"}</Td>
                        <Td muted>{(o.created_at || "").slice(0, 10)}</Td>
                      </tr>
                    ))}
                    {data.orders.length === 0 && (
                      <tr><td colSpan="6" className="px-5 py-8 text-center text-sm text-[#8A7D71]">No orders yet.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
              {tab === "Messages" && (
                <table className="w-full" data-testid="messages-table">
                  <thead><tr className="border-b border-[#C6A769]/20">
                    <Th>Name</Th><Th>Email</Th><Th>Message</Th><Th>Date</Th>
                  </tr></thead>
                  <tbody>
                    {data.messages.map((m, i) => (
                      <tr key={i} className="border-b border-[#4A3F35]/5 last:border-0 align-top">
                        <Td>{m.name}</Td>
                        <Td>{m.email}</Td>
                        <Td muted><span className="block max-w-md whitespace-pre-wrap">{m.message}</span></Td>
                        <Td muted>{(m.created_at || "").slice(0, 10)}</Td>
                      </tr>
                    ))}
                    {data.messages.length === 0 && (
                      <tr><td colSpan="4" className="px-5 py-8 text-center text-sm text-[#8A7D71]">No messages yet.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
              {tab === "Subscribers" && (
                <table className="w-full" data-testid="subscribers-table">
                  <thead><tr className="border-b border-[#C6A769]/20">
                    <Th>Email</Th><Th>Source</Th><Th>Subscribed</Th>
                  </tr></thead>
                  <tbody>
                    {data.subscribers.map((s, i) => (
                      <tr key={i} className="border-b border-[#4A3F35]/5 last:border-0">
                        <Td>{s.email}</Td>
                        <Td muted>{s.source}</Td>
                        <Td muted>{(s.created_at || "").slice(0, 10)}</Td>
                      </tr>
                    ))}
                    {data.subscribers.length === 0 && (
                      <tr><td colSpan="3" className="px-5 py-8 text-center text-sm text-[#8A7D71]">No subscribers yet.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
